import { message, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { REQUEST_LOADING_STATES } from "../../../../../helpers/constant";
import { getFanDiscovers, saveScrollPosition, saveCurrentDiscoverIndex } from "../../../../../redux/discovers/discoversSlice";
import { AppDispatch, StoreState } from "../../../../../redux/store";
import SpinnerComponent from "../../../../SpinnerComponent/SpinnerComponent";
import useFetchDiscovers from "../../../hooks/useFetchDiscovers";
import SwiperItemComponent from "../SwiperItemComponent";
import SwiperTopOverlayComponent from "../SwiperItemComponent/SwiperTopOverlayComponent";
import "./index.less";
import { useSelector } from "react-redux";
import { discoverService, utilService } from "../../../../../services";
import { IFanDiscoverDto } from "../../../../../helpers/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import PresentationAdComponent from "../../../../FanHomeAdComponent/PresentationAdComponent";

const S3_PRESENTATION_AD_URL = "https://twizz-scripts.s3.us-east-1.amazonaws.com/presentation.txt";

type PresentationVideoSwiperProps = {
  searchClick?: () => void;
  username?: string;
  presentationOfSingleCreator?: boolean;
  isVisible?: boolean;
  shouldShow?: boolean;
};

const PresentationVideoSwiper: React.FC<PresentationVideoSwiperProps> = ({
  searchClick,
  username,
  presentationOfSingleCreator = false,
  isVisible = true,
  shouldShow,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const { fanDiscoversPage: page, scrollPosition, currentDiscoverIndex } = useSelector((state: StoreState) => state.discovers);

  const showAds = useFeatureFlagEnabled("discover_ads");

  // Default to true if undefined (still loading)
  const shouldShowAds = showAds !== false;

  useEffect(() => {
    console.log("Feature flag loaded - showAds:", showAds, "shouldShowAds:", shouldShowAds);
  }, [showAds, shouldShowAds]);


  const [singleCreatorDiscovers, setSingleCreatorDiscovers] = useState<IFanDiscoverDto[]>([]);
  const [singleCreatorLoading, setSingleCreatorLoading] = useState<string>("idle");
  const [singleCreatorError, setSingleCreatorError] = useState<string | null>(null);
  const [singleCreatorTotal, setSingleCreatorTotal] = useState<number>(0);
  const [singleCreatorPage, setSingleCreatorPage] = useState<number>(1);

  const storeDiscovers = useFetchDiscovers(page, presentationOfSingleCreator ? undefined : username);

  const fanDiscovers = presentationOfSingleCreator ? singleCreatorDiscovers : storeDiscovers.fanDiscovers;
  const loadingFanDiscovers = presentationOfSingleCreator ? singleCreatorLoading : storeDiscovers.loadingFanDiscovers;
  const errorFanDiscovers = presentationOfSingleCreator ? singleCreatorError : storeDiscovers.errorFanDiscovers;
  const total = presentationOfSingleCreator ? singleCreatorTotal : storeDiscovers.total;

  const [globalMuteState, setGlobalMuteState] = useState(true);
  const [globalShowMuteButton, setGlobalShowMuteButton] = useState(true);
  const swiperRef = useRef<HTMLDivElement>(null);
  const [hasRestoredScroll, setHasRestoredScroll] = useState(false);
  const isRestoringRef = useRef(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [adContent, setAdContent] = useState<string>("");

  useEffect(() => {
    if (presentationOfSingleCreator && username && singleCreatorDiscovers.length === 0) {
      setSingleCreatorLoading("pending");
      discoverService
        .getFanDiscovers({ page: singleCreatorPage, username })
        .then((response) => {
          setSingleCreatorDiscovers(response.data);
          setSingleCreatorTotal(response.total);
          setSingleCreatorLoading("succeeded");
        })
        .catch((error) => {
          setSingleCreatorError(error || "Something went wrong");
          setSingleCreatorLoading("failed");
        });
    }
  }, [presentationOfSingleCreator, username, singleCreatorPage, singleCreatorDiscovers.length]);

  const handleMuteChange = useCallback(
    (newMuteState: boolean) => {
      // Only update if the state actually changed
      if (globalMuteState !== newMuteState) {
        setGlobalMuteState(newMuteState);
      }
    },
    [globalMuteState]
  );

  const handleShowMuteButtonChange = useCallback(
    (newShowMuteButtonState: boolean) => {
      // Only update if the state actually changed
      if (globalShowMuteButton !== newShowMuteButtonState) {
        setGlobalShowMuteButton(newShowMuteButtonState);
      }
    },
    [globalShowMuteButton]
  );

  const updateHeaderHeight = () => {
    const headerElement = document.querySelector(".header-info-discover-container");
    if (headerElement) {
      const headerHeight = headerElement.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${headerHeight}px`);
    }
  };

  const updateFooterHeight = () => {
    const footerElement = document.querySelector(".footer");
    if (footerElement) {
      const footerHeight = footerElement.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--footer-height", `${footerHeight}px`);
    }
  };

  useEffect(() => {
    updateHeaderHeight();
    updateFooterHeight();

    const headerElement = document.querySelector(".header-info-discover-container");
    if (headerElement) {
      const resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(headerElement);
      return () => resizeObserver.disconnect();
    }
  }, []);

  const updateViewportHeight = useCallback(() => {
    // Get the actual viewport height
    const vh = window.innerHeight;
    // Set the viewport height as a CSS variable
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    setViewportHeight(vh);
  }, []);

  useEffect(() => {
    // Initial viewport height update
    updateViewportHeight();

    // Update on resize and orientation change
    window.addEventListener("resize", updateViewportHeight);
    window.addEventListener("orientationchange", () => {
      // Add a small delay to ensure the browser UI has settled
      setTimeout(updateViewportHeight, 100);
    });

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
    };
  }, [updateViewportHeight]);

  useEffect(() => {
    const fetchAdContent = async () => {
      console.log("Fetching ad content for Discover from:", S3_PRESENTATION_AD_URL);
      const content = await utilService.getAdContent(S3_PRESENTATION_AD_URL);
      if (content) {
        console.log("Ad content successfully fetched for Discover");
        setAdContent(content);
      } else {
        console.warn("Failed to fetch ad content for Discover");
      }
    };
    fetchAdContent();
  }, []);

  useEffect(() => {
    if (errorFanDiscovers) {
      message.error(errorFanDiscovers);
    }
  }, [errorFanDiscovers]);

  useEffect(() => {
    const swiperElement = swiperRef.current;
    if (!swiperElement) {
      return;
    }

    let scrollSettleTimer: number | null = null;

    const updateActiveSlide = () => {
      if (scrollSettleTimer) {
        window.clearTimeout(scrollSettleTimer);
      }

      scrollSettleTimer = window.setTimeout(() => {
        const slides = swiperElement.querySelectorAll(".swiper-slide");
        let nextIndex = 0;
        let minDiff = Infinity;

        slides.forEach((slide) => {
          const slideTop = (slide as HTMLElement).offsetTop;
          const diff = Math.abs(slideTop - swiperElement.scrollTop);
          if (diff < minDiff) {
            minDiff = diff;
            const indexAttr = slide.getAttribute("data-slide-index");
            if (indexAttr) {
              nextIndex = parseInt(indexAttr, 10);
            }
          }
        });

        setActiveSlideIndex((prev) => (prev === nextIndex ? prev : nextIndex));
      }, 120);
    };

    updateActiveSlide();
    swiperElement.addEventListener("scroll", updateActiveSlide, { passive: true });

    return () => {
      if (scrollSettleTimer) {
        window.clearTimeout(scrollSettleTimer);
      }
      swiperElement.removeEventListener("scroll", updateActiveSlide);
    };
  }, [viewportHeight, fanDiscovers.length]);

  // Restore scroll position instantly without animation when component mounts
  useEffect(() => {
    if (fanDiscovers.length > 0 && scrollPosition > 0 && swiperRef.current && !hasRestoredScroll && !isRestoringRef.current) {
      isRestoringRef.current = true;

      // Temporarily disable scroll-behavior to prevent animation
      const swiperElement = swiperRef.current;
      const originalScrollBehavior = swiperElement.style.scrollBehavior;
      swiperElement.style.scrollBehavior = "auto";

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (swiperRef.current) {
          // Instantly set scroll position without animation
          swiperRef.current.scrollTop = scrollPosition;
          setHasRestoredScroll(true);

          // Restore original scroll behavior after a short delay
          setTimeout(() => {
            if (swiperRef.current) {
              swiperRef.current.style.scrollBehavior = originalScrollBehavior;
            }
            isRestoringRef.current = false;
          }, 100);
        }
      });
    } else if (fanDiscovers.length > 0 && scrollPosition === 0) {
      setHasRestoredScroll(true);
    }
  }, [fanDiscovers.length, scrollPosition, hasRestoredScroll]);

  // Save scroll position when unmounting (user navigates away)
  useEffect(() => {
    return () => {
      if (swiperRef.current) {
        const currentScroll = swiperRef.current.scrollTop;
        dispatch(saveScrollPosition(currentScroll));

        // Calculate which discover index based on scroll position
        const slides = swiperRef.current.querySelectorAll(".swiper-slide");
        let discoverIndex = 0;
        let minDiff = Infinity;

        slides.forEach((slide) => {
          if (swiperRef.current) {
            const slideTop = (slide as HTMLElement).offsetTop;
            const diff = Math.abs(slideTop - swiperRef.current.scrollTop);
            if (diff < minDiff) {
              minDiff = diff;
              const indexAttr = slide.getAttribute("data-slide-index");
              if (indexAttr) {
                discoverIndex = parseInt(indexAttr, 10);
              }
            }
          }
        });

        dispatch(saveCurrentDiscoverIndex(discoverIndex));
      }
    };
  }, [dispatch, viewportHeight]);

  const loadMoreDiscovers = useCallback(() => {
    const hasMore = fanDiscovers.length < total!;
    if (loadingFanDiscovers === "pending" || !hasMore) return;

    if (presentationOfSingleCreator && username) {
      setSingleCreatorLoading("pending");
      const nextPage = singleCreatorPage + 1;
      discoverService
        .getFanDiscovers({ page: nextPage, username })
        .then((response) => {
          setSingleCreatorDiscovers((prev) => [...prev, ...response.data]);
          setSingleCreatorTotal(response.total);
          setSingleCreatorPage(nextPage);
          setSingleCreatorLoading("succeeded");
        })
        .catch((error) => {
          setSingleCreatorError(error || "Something went wrong");
          setSingleCreatorLoading("failed");
        });
    } else {
      dispatch(getFanDiscovers({ page, username }));
    }
  }, [dispatch, loadingFanDiscovers, page, total, username, fanDiscovers.length, presentationOfSingleCreator, singleCreatorPage]);

  // Memoize the swiper items to prevent unnecessary re-renders during swiping
  const swiperItems = useMemo(() => {
    const items: React.ReactNode[] = [];
    fanDiscovers.forEach((discover, index) => {
      const contentSlideIndex = items.length;
      items.push(
        <div className="swiper-slide" key={discover._id} data-slide-index={contentSlideIndex}>
          <SwiperItemComponent
            discover={discover}
            presentationOfSingleCreator={presentationOfSingleCreator}
            index={index}
            total={fanDiscovers.length}
            loadMoreDiscovers={loadMoreDiscovers}
            globalMuteState={globalMuteState}
            handleMuteChange={handleMuteChange}
            globalShowMuteButton={globalShowMuteButton}
            handleShowMuteButtonChange={handleShowMuteButtonChange}
            shouldShow={shouldShow || false}
          />
        </div>
      );
      if ((index + 1) % 5 === 0 && shouldShowAds) {
        const adSlideIndex = items.length;
        items.push(
          <div className="swiper-slide swiper-slide-ad" key={`ad-${index}`} data-slide-index={adSlideIndex}>
            {adContent && <PresentationAdComponent adContent={adContent} key={`discover-ad-${adSlideIndex}`} />}
          </div>
        );
      }
    });
    return items;
  }, [
    fanDiscovers,
    presentationOfSingleCreator,
    loadMoreDiscovers,
    globalMuteState,
    handleMuteChange,
    globalShowMuteButton,
    handleShowMuteButtonChange,
    shouldShow,
    showAds,
    isVisible,
    searchClick,
    adContent,
  ]);

  return (
    <>
      <div className={`swiper-wrapper ${isVisible ? "" : "hidden"}`}>
        <div className="swiper-container">
          {searchClick && isVisible && <SwiperTopOverlayComponent searchClick={searchClick} shouldShow={shouldShow} />}
          {fanDiscovers.length !== 0 ? (
            <div
              ref={swiperRef}
              className="swiper"
              style={{
                height: `${viewportHeight}px`,
              }}
            >
              {swiperItems}
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                position: "absolute",
                top: "64px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {loadingFanDiscovers === REQUEST_LOADING_STATES.PENDING ? (
                <SpinnerComponent />
              ) : (
                <Typography.Text className="no-creator-found">{t("no-media-found")}</Typography.Text>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PresentationVideoSwiper;
